import { T } from 'gt-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  useEmailTemplates,
  useEmailTemplateBuiltins,
} from 'src/api/rpc/hooks/communications/useEmailTemplates';
import { TemplateLinkButton } from 'src/pages/authenticated/workflows/components/config-renderers/shared/TemplateLinkButton';
import {
  CATEGORY_BY_GROUP,
  CUSTOM_MESSAGE_VALUE,
} from './notifyMessageTemplates';

/**
 * Message picker for the ad-hoc Notify dialog: the built-in notification
 * templates grouped as "Sales orders" / "Discount requests" (plus the org's
 * own editable copies), and "Custom" for free-form text. Values are
 * `builtin:<key>` refs or org template ids.
 */
export function NotifyMessageTemplateSelect({
  id,
  value,
  onChange,
}: {
  id?: string;
  /** A template ref/id, or `CUSTOM_MESSAGE_VALUE`. */
  value: string;
  onChange: (value: string) => void;
}) {
  const { data: builtinsData } = useEmailTemplateBuiltins();
  const { data: templatesData } = useEmailTemplates();

  const builtins = builtinsData?.builtins ?? [];
  const orderBuiltins = builtins.filter(
    (b) => b.category === CATEGORY_BY_GROUP.orders,
  );
  const discountBuiltins = builtins.filter(
    (b) => b.category === CATEGORY_BY_GROUP.discounts,
  );
  const orgTemplates = (templatesData?.templates ?? []).filter(
    (t) => t.isActive || t.id === value,
  );
  const isTemplate = value !== CUSTOM_MESSAGE_VALUE;

  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={id}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CUSTOM_MESSAGE_VALUE}>
              <T context="notify dialog message option: free-form title and message">
                Custom message
              </T>
            </SelectItem>
            {orderBuiltins.length > 0 && (
              <>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>
                    <T context="notify dialog template group heading">
                      Sales orders
                    </T>
                  </SelectLabel>
                  {orderBuiltins.map((b) => (
                    <SelectItem key={b.key} value={`builtin:${b.key}`}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </>
            )}
            {discountBuiltins.length > 0 && (
              <>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>
                    <T context="notify dialog template group heading">
                      Discount requests
                    </T>
                  </SelectLabel>
                  {discountBuiltins.map((b) => (
                    <SelectItem key={b.key} value={`builtin:${b.key}`}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </>
            )}
            {orgTemplates.length > 0 && (
              <>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>
                    <T context="notify dialog template group heading: the org's own email templates">
                      Organization templates
                    </T>
                  </SelectLabel>
                  {orgTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                      {!t.isActive && (
                        <span className="text-muted-foreground ml-1">
                          <T context="inactive email template suffix">
                            (inactive)
                          </T>
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      {isTemplate ? <TemplateLinkButton templateRef={value} /> : null}
    </div>
  );
}
